import type { CeoInflowPlanRow } from '@shared/api';
import { Badge } from '@shared/ui/Badge';
import { Button } from '@shared/ui/Button';
import { formatDateRu, formatMoney, INFLOW_STATUS_LABELS } from '../ceoFormat';
import {
  ActionColumn,
  CollectionPctText,
  ContractNumberText,
  CustomerNameText,
  DeductionSubtext,
  DeductionText,
  DelayDaysText,
  DocNumberText,
  DueDateText,
  EditInputGroup,
  Input,
  ProjectNameText,
  ReceivableBalanceText,
  ReceivedAmountText,
  ResponsibleText,
  RowEditActions,
  Section,
  Table,
  TableWrap,
  Title,
} from './styles';

interface InflowsTableProps {
  rows: CeoInflowPlanRow[];
  editingRowId: string | null;
  editKs3Amount: string;
  setEditKs3Amount: (v: string) => void;
  editKs3No: string;
  setEditKs3No: (v: string) => void;
  editActualAmount: string;
  setEditActualAmount: (v: string) => void;
  can_write: boolean;
  onStartEdit: (row: CeoInflowPlanRow) => void;
  onCancelEdit: () => void;
  onSaveRowFact: (rowId: string) => void;
}

export function InflowsTable({
  rows,
  editingRowId,
  editKs3Amount,
  setEditKs3Amount,
  editKs3No,
  setEditKs3No,
  editActualAmount,
  setEditActualAmount,
  can_write,
  onStartEdit,
  onCancelEdit,
  onSaveRowFact,
}: InflowsTableProps) {
  return (
    <Section>
      <Title>Пообъектная ведомость: План БДДС → Подписанные КС-3 → Фактически поступило</Title>
      <TableWrap>
        <Table>
          <thead>
            <tr>
              <th>Строительный объект / Заказчик</th>
              <th>Договор / КС-3</th>
              <th>План БДДС</th>
              <th>Подписано КС-3 (брутто)</th>
              <th>Удержания (аванс + ГУ)</th>
              <th>Ожидается по КС-3 (нетто)</th>
              <th>Фактически поступило</th>
              <th>Остаток к получению</th>
              <th>Статус / Действие</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const isEditing = editingRowId === r.id;
              return (
                <tr key={r.id}>
                  <td>
                    <ProjectNameText>{r.project_name}</ProjectNameText>
                    <CustomerNameText>{r.customer_name}</CustomerNameText>
                    {r.responsible_person && (
                      <ResponsibleText>{r.responsible_person}</ResponsibleText>
                    )}
                  </td>
                  <td>
                    <ContractNumberText>{r.contract_number}</ContractNumberText>
                    <DocNumberText>
                      {r.ks3_doc_number ?? 'Проект КС-3'}
                    </DocNumberText>
                    {r.expected_payment_date && (
                      <DueDateText>
                        Срок оплаты: {formatDateRu(r.expected_payment_date)}
                      </DueDateText>
                    )}
                  </td>
                  <td>{formatMoney(r.bdds_planned_amount)}</td>
                  <td>
                    {isEditing ? (
                      <Input
                        type="number"
                        value={editKs3Amount}
                        onChange={(e) => setEditKs3Amount(e.target.value)}
                        placeholder="Подписано по КС-3, ₽"
                      />
                    ) : r.ks3_signed_amount > 0 ? (
                      formatMoney(r.ks3_signed_amount)
                    ) : (
                      '—'
                    )}
                  </td>
                  <td>
                    <DeductionText>
                      Аванс: {formatMoney(r.advance_deduction)}
                    </DeductionText>
                    <DeductionSubtext>
                      ГУ: {formatMoney(r.retention_deduction)}
                    </DeductionSubtext>
                  </td>
                  <td>
                    <strong>{formatMoney(r.expected_net_inflow)}</strong>
                  </td>
                  <td>
                    {isEditing ? (
                      <EditInputGroup>
                        <Input
                          type="number"
                          value={editActualAmount}
                          onChange={(e) => setEditActualAmount(e.target.value)}
                          placeholder="Факт прихода, ₽"
                        />
                        <Input
                          type="text"
                          value={editKs3No}
                          onChange={(e) => setEditKs3No(e.target.value)}
                          placeholder="№ КС-3"
                        />
                      </EditInputGroup>
                    ) : (
                      <>
                        <ReceivedAmountText $hasReceived={r.actual_received_amount > 0}>
                          {formatMoney(r.actual_received_amount)}
                        </ReceivedAmountText>
                        <CollectionPctText>
                          {r.ks3_collection_pct.toFixed(1)}% от КС-3
                        </CollectionPctText>
                      </>
                    )}
                  </td>
                  <td>
                    <ReceivableBalanceText $hasBalance={r.ks3_receivable_balance > 0}>
                      {formatMoney(r.ks3_receivable_balance)}
                    </ReceivableBalanceText>
                    {r.delay_days > 0 && (
                      <DelayDaysText>
                        просрочка {r.delay_days} дн.
                      </DelayDaysText>
                    )}
                  </td>
                  <td>
                    <ActionColumn>
                      <Badge
                        $tone={
                          r.status === 'paid_in_full'
                            ? 'success'
                            : r.status === 'overdue_receivable'
                              ? 'danger'
                              : r.status === 'partially_paid'
                                ? 'info'
                                : 'warning'
                        }
                      >
                        {INFLOW_STATUS_LABELS[r.status] ?? r.status}
                      </Badge>
                      {can_write &&
                        (isEditing ? (
                          <RowEditActions>
                            <Button
                              $size="sm"
                              onClick={() => onSaveRowFact(r.id)}
                            >
                              Сохранить
                            </Button>
                            {r.expected_net_inflow > 0 && (
                              <Button
                                $size="sm"
                                $variant="secondary"
                                type="button"
                                onClick={() =>
                                  setEditActualAmount(String(r.expected_net_inflow))
                                }
                                title="Подставить полную сумму к оплате по КС-3 (нетто)"
                              >
                                100% КС-3
                              </Button>
                            )}
                            <Button
                              $size="sm"
                              $variant="ghost"
                              onClick={onCancelEdit}
                            >
                              ✕
                            </Button>
                          </RowEditActions>
                        ) : (
                          <Button
                            $size="sm"
                            $variant="ghost"
                            onClick={() => onStartEdit(r)}
                          >
                            Отметить приход / КС-3
                          </Button>
                        ))}
                    </ActionColumn>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      </TableWrap>
    </Section>
  );
}
